import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLazyAssetsTypesCountsQuery } from '~/api/assets-api/assets-api';
import type { AppState } from '~/reducers';
import {
  setIsLoaded,
  setIsLoading,
  importAssetsTotalCountResponse,
} from '~/domain/assets/store/assets-counts/reducer';

const useAssetsTypesCounts = () => {
  const dispatch = useDispatch();
  const assetsCounts = useSelector((state: AppState) => state.assetsCounts);
  const [refetch, { data, isError, isSuccess }] = useLazyAssetsTypesCountsQuery({
    refetchOnFocus: true,
    refetchOnReconnect: true,
  });
  const loading = assetsCounts.isLoading;
  const assetTypesCounts = assetsCounts.isLoaded ? assetsCounts : undefined;

  useEffect(() => {
    if (!assetsCounts.isLoaded) {
      dispatch(setIsLoading(true));
      refetch();
    }
  }, [assetsCounts.isLoaded, dispatch, refetch]);

  useEffect(() => {
    if (isSuccess && data !== undefined) {
      dispatch(importAssetsTotalCountResponse(data));
      dispatch(setIsLoading(false));
      dispatch(setIsLoaded(true));
    }
  }, [data, isSuccess, dispatch]);

  useEffect(() => {
    if (isError) {
      dispatch(setIsLoading(false));
      dispatch(setIsLoaded(true));
    }
  }, [dispatch, isError]);

  useEffect(() => {
    if (assetsCounts.assetDeletedDate || assetsCounts.assetCreatedDate) {
      setTimeout(() => {
        refetch();
      }, 1000);
    }
  }, [assetsCounts.assetDeletedDate, assetsCounts.assetCreatedDate, refetch]);

  return {
    refetch,
    isLoading: loading,
    isError,
    assetTypesCounts,
    hasAssets:
      assetTypesCounts?.totalAssetsCount !== undefined &&
      assetTypesCounts.totalAssetsCount > 0 &&
      !loading,
    hasNoAssets: assetTypesCounts?.totalAssetsCount === 0 && !loading,
  };
};

export { useAssetsTypesCounts };
